/**
 * Gestión → Administración → Cotización (nueva)
 * (`/gestion/cotizaciones/nueva`).
 *
 * Puerto nativo de `Cotizacion::registrar` + `views/menu/cotizacion/registrar.php`
 * y de `src/cotizacion/registrar.js`: alta de una cotización con productos
 * (combustibles) y métodos de pago, con el desglose de IGV y la validación de que
 * la suma de pagos cuadre con el total.
 */

import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { AdministracionNav } from '../components/administracion-nav';
import { monto } from '../gestion-formato';
import { useCatalogosCotizacion, useCrearCotizacion } from '../cotizacion.api';
import type { NuevaCotizacion, NuevoMetodoPagoCotizacion } from '../cotizacion.types';

/** Fila de producto del formulario (con campos para pintarlo). */
interface ProductoFila {
  producto_id: number;
  nombre: string;
  unidad: string;
  precio_unitario: number;
  cantidad: number;
  total: number;
}

/** Método de pago del formulario (con etiquetas para pintarlo). */
interface MetodoPagoFila extends NuevoMetodoPagoCotizacion {
  metodo_nombre: string;
  banco_nombre: string;
}

function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function numero(valor: string): number {
  const cifra = Number(valor);

  return Number.isFinite(cifra) ? cifra : 0;
}

export function CotizacionFormPage() {
  useDocumentTitle('Gestión · Nueva cotización');

  const navegar = useNavigate();
  const catalogos = useCatalogosCotizacion();
  const crear = useCrearCotizacion();

  const [serie, setSerie] = useState('');
  const [fechaEmision, setFechaEmision] = useState(hoyIso);
  const [tiempoValidez, setTiempoValidez] = useState('');
  const [tiempoEntrega, setTiempoEntrega] = useState('');
  const [terminoPagoId, setTerminoPagoId] = useState<string>('');
  const [numeroCuenta, setNumeroCuenta] = useState('');
  const [tipoCambio, setTipoCambio] = useState('3.728');
  const [clienteId, setClienteId] = useState<string>('');
  const [clienteDireccion, setClienteDireccion] = useState('');
  const [contacto, setContacto] = useState('-');
  const [telefono, setTelefono] = useState('-');
  const [observacion, setObservacion] = useState('');
  const [informacionReferencial, setInformacionReferencial] = useState('');
  const [exonerado, setExonerado] = useState(false);

  const [productos, setProductos] = useState<ProductoFila[]>([]);
  const [metodosPago, setMetodosPago] = useState<MetodoPagoFila[]>([]);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [productoId, setProductoId] = useState<string>('');
  const [precioUnitario, setPrecioUnitario] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [totalManual, setTotalManual] = useState('');

  const [error, setError] = useState<string | null>(null);

  const empresa = catalogos.data?.empresa;

  // Serie y término de pago por defecto cuando cargan los catálogos.
  useEffect(() => {
    if (!serie && empresa?.serie_cotizacion) setSerie(empresa.serie_cotizacion);

    const primerTermino = catalogos.data?.terminos_pago[0];
    if (!terminoPagoId && primerTermino) {
      setTerminoPagoId(String(primerTermino.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogos.data]);

  const productoElegido = catalogos.data?.productos?.find((p) => String(p.id) === productoId);

  const elegirCliente = (valor: string) => {
    setClienteId(valor);
    const cliente = catalogos.data?.clientes?.find((c) => String(c.id) === valor);
    setClienteDireccion(cliente?.direccion ?? '');
  };

  const elegirProducto = (valor: string) => {
    setProductoId(valor);
    const producto = catalogos.data?.productos?.find((p) => String(p.id) === valor);
    setPrecioUnitario(producto ? String(producto.precio_venta) : '');
    setCantidad('');
    setTotalManual('');
  };

  const agregarProducto = () => {
    const producto = productoElegido;

    if (!producto) return;

    const precio = numero(precioUnitario);
    const cant = numero(cantidad);
    const total = numero(totalManual) || precio * cant;

    if (productos.some((p) => p.producto_id === producto.id)) {
      setError('No es posible añadir dos veces el mismo producto.');
      return;
    }

    setProductos((anteriores) => [
      ...anteriores,
      {
        producto_id: producto.id,
        nombre: producto.nombre,
        unidad: producto.unidad_medida,
        precio_unitario: precio,
        cantidad: cant,
        total,
      },
    ]);

    setModalAbierto(false);
    setError(null);
    setProductoId('');
    setPrecioUnitario('');
    setCantidad('');
    setTotalManual('');
  };

  const agregarMetodoPago = () => {
    const terminos = catalogos.data?.terminos_pago ?? [];
    const bancos = catalogos.data?.bancos ?? [];

    setMetodosPago((anteriores) => [
      ...anteriores,
      {
        metodo_termino_pago_id: terminos[0]?.id ?? 0,
        destino_banco_id: bancos[0]?.id ?? 0,
        referencia: '',
        monto: 0,
        metodo_nombre: terminos[0]?.descripcion ?? '',
        banco_nombre: bancos[0]?.descripcion ?? '',
      },
    ]);
  };

  const actualizarMetodo = (indice: number, parcial: Partial<MetodoPagoFila>) => {
    setMetodosPago((anteriores) =>
      anteriores.map((metodo, i) => (i === indice ? { ...metodo, ...parcial } : metodo)),
    );
  };

  const totales = useMemo(() => {
    const total = productos.reduce((suma, producto) => suma + producto.total, 0);
    const factor = exonerado ? 1 : 1.18;
    const subTotal = total / factor;
    const igv = total - subTotal;

    return { total, subTotal, igv };
  }, [productos, exonerado]);

  const totalMetodos = metodosPago.reduce((suma, metodo) => suma + (metodo.monto ?? 0), 0);

  const registrar = () => {
    setError(null);

    if (productos.length === 0) {
      setError('Debe agregar productos a la cotización.');
      return;
    }

    if (Math.abs(totalMetodos - totales.total) > 0.009) {
      setError('La suma de los montos de pago debe ser igual al total a pagar.');
      return;
    }

    const datos: NuevaCotizacion = {
      serie,
      fecha_emision: fechaEmision,
      tiempo_validez: tiempoValidez || null,
      tiempo_entrega: tiempoEntrega || null,
      termino_pago_id: Number(terminoPagoId) || 0,
      numero_cuenta: numeroCuenta || null,
      moneda: 'SOLES',
      tipo_cambio: numero(tipoCambio),
      cliente_id: Number(clienteId) || 0,
      cliente_direccion: clienteDireccion || null,
      contacto: contacto || null,
      telefono: telefono || null,
      observacion: observacion || null,
      informacion_referencial: informacionReferencial || null,
      exonerado,
      productos: productos.map((producto) => ({
        producto_id: producto.producto_id,
        unidad: producto.unidad || null,
        precio_unitario: producto.precio_unitario,
        cantidad: producto.cantidad,
        total: producto.total,
      })),
      metodos_pago: metodosPago.map((metodo) => ({
        metodo_termino_pago_id: metodo.metodo_termino_pago_id,
        destino_banco_id: metodo.destino_banco_id,
        referencia: metodo.referencia || null,
        monto: metodo.monto,
      })),
    };

    crear.mutate(datos, {
      onSuccess: () => navegar('/gestion/cotizaciones'),
      onError: (err) => {
        setError(err instanceof ApiError ? err.message : 'No se pudo registrar la cotización.');
      },
    });
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Nueva cotización</h1>
          <p className="mt-1 text-sm text-ink-500">
            Agrega productos (combustibles) y métodos de pago, y registra la cotización.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          leftIcon={<ArrowLeft className="size-4" aria-hidden="true" />}
          onClick={() => navegar('/gestion/cotizaciones')}
        >
          Volver
        </Button>
      </header>

      <AdministracionNav active="cotizacion" />

      {catalogos.isLoading ? <Skeleton className="h-96 w-full" /> : null}

      {catalogos.isError ? (
        <Alert variant="error" title="No se pudieron cargar los catálogos">
          {catalogos.error instanceof ApiError ? catalogos.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="Revisa la cotización">
          {error}
        </Alert>
      ) : null}

      {catalogos.data ? (
        <>
          <Card>
            <CardContent className="p-4">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-ink-100 pb-3">
                <div>
                  <p className="text-base font-semibold text-ink-900">
                    {empresa?.razon_social ?? 'Empresa'} - {empresa?.ruc ?? ''}
                  </p>
                  <p className="text-xs text-ink-500">{empresa?.direccion ?? ''}</p>
                </div>

                <Checkbox
                  label="Exonerado (Ley 27037)"
                  checked={exonerado}
                  onChange={(evento) => setExonerado(evento.target.checked)}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Campo etiqueta="Serie">
                  <Input value={serie} onChange={(evento) => setSerie(evento.target.value)} />
                </Campo>

                <Campo etiqueta="Fecha emisión">
                  <Input
                    type="date"
                    max={hoyIso()}
                    value={fechaEmision}
                    onChange={(evento) => setFechaEmision(evento.target.value)}
                  />
                </Campo>

                <Campo etiqueta="Tiempo de validez">
                  <Input value={tiempoValidez} onChange={(evento) => setTiempoValidez(evento.target.value)} />
                </Campo>

                <Campo etiqueta="Tiempo de entrega">
                  <Input value={tiempoEntrega} onChange={(evento) => setTiempoEntrega(evento.target.value)} />
                </Campo>

                <Campo etiqueta="Término de pago">
                  <Select value={terminoPagoId} onChange={(evento) => setTerminoPagoId(evento.target.value)}>
                    {catalogos.data.terminos_pago.map((termino) => (
                      <option key={termino.id} value={termino.id}>
                        {termino.descripcion}
                      </option>
                    ))}
                  </Select>
                </Campo>

                <Campo etiqueta="Número de cuenta">
                  <Input value={numeroCuenta} onChange={(evento) => setNumeroCuenta(evento.target.value)} />
                </Campo>

                <Campo etiqueta="Moneda">
                  <Select value="SOLES" onChange={() => undefined}>
                    <option value="SOLES">Soles</option>
                  </Select>
                </Campo>

                <Campo etiqueta="Tipo de cambio">
                  <Input
                    type="number"
                    step="0.001"
                    value={tipoCambio}
                    onChange={(evento) => setTipoCambio(evento.target.value)}
                  />
                </Campo>

                <div className="sm:col-span-2 lg:col-span-1">
                  <Campo etiqueta="Cliente">
                    <Select value={clienteId} onChange={(evento) => elegirCliente(evento.target.value)}>
                      <option value="">Seleccionar</option>
                      {catalogos.data.clientes.map((cliente) => (
                        <option key={cliente.id} value={cliente.id}>
                          {cliente.numero_doc ?? ''} - {cliente.nombre ?? 'Sin nombre'}
                        </option>
                      ))}
                    </Select>
                  </Campo>
                </div>

                <div className="sm:col-span-2">
                  <Campo etiqueta="Dirección">
                    <Input
                      value={clienteDireccion}
                      onChange={(evento) => setClienteDireccion(evento.target.value)}
                    />
                  </Campo>
                </div>

                <Campo etiqueta="Contacto">
                  <Input value={contacto} onChange={(evento) => setContacto(evento.target.value)} />
                </Campo>

                <Campo etiqueta="Teléfono">
                  <Input value={telefono} onChange={(evento) => setTelefono(evento.target.value)} />
                </Campo>

                <div className="sm:col-span-2 lg:col-span-1">
                  <Campo etiqueta="Observación">
                    <Input value={observacion} onChange={(evento) => setObservacion(evento.target.value)} />
                  </Campo>
                </div>

                <div className="sm:col-span-2">
                  <Campo etiqueta="Información referencial">
                    <Input
                      value={informacionReferencial}
                      onChange={(evento) => setInformacionReferencial(evento.target.value)}
                    />
                  </Campo>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-ink-800">Productos</h2>
                <Button size="sm" leftIcon={<Plus className="size-4" aria-hidden="true" />} onClick={() => setModalAbierto(true)}>
                  Agregar producto
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                    <tr>
                      <th className="py-2 pr-3 font-semibold">Descripción</th>
                      <th className="py-2 pr-3 font-semibold">Unidad</th>
                      <th className="py-2 pr-3 font-semibold text-right">Cantidad</th>
                      <th className="py-2 pr-3 font-semibold text-right">Precio</th>
                      <th className="py-2 pr-3 font-semibold text-right">Total</th>
                      <th className="py-2 pr-3 font-semibold text-right">Quitar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-50">
                    {productos.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-sm text-ink-400">
                          Todavía no hay productos agregados.
                        </td>
                      </tr>
                    ) : (
                      productos.map((producto) => (
                        <tr key={producto.producto_id}>
                          <td className="py-2 pr-3 text-ink-700">{producto.nombre}</td>
                          <td className="py-2 pr-3 text-ink-500">{producto.unidad}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{monto(producto.cantidad)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            S/ {monto(producto.precio_unitario)}
                          </td>
                          <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                            S/ {monto(producto.total)}
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Quitar producto"
                              onClick={() =>
                                setProductos((anteriores) =>
                                  anteriores.filter((p) => p.producto_id !== producto.producto_id),
                                )
                              }
                            >
                              <Trash2 className="size-4" aria-hidden="true" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 ml-auto w-full max-w-sm space-y-1 text-sm">
                <FilaTotal etiqueta="Subtotal" valor={`S/ ${monto(totales.subTotal)}`} />
                <FilaTotal etiqueta="IGV" valor={`S/ ${monto(totales.igv)}`} />
                <FilaTotal etiqueta="TOTAL A PAGAR" valor={`S/ ${monto(totales.total)}`} destacado />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-ink-800">Métodos de pago</h2>
                <Button size="sm" variant="outline" leftIcon={<Plus className="size-4" aria-hidden="true" />} onClick={agregarMetodoPago}>
                  Agregar método
                </Button>
              </div>

              {metodosPago.length === 0 ? (
                <p className="py-6 text-center text-sm text-ink-400">
                  Agrega al menos un método de pago que cuadre con el total.
                </p>
              ) : (
                <div className="space-y-3">
                  {metodosPago.map((metodo, indice) => (
                    <div key={indice} className="grid gap-2 rounded-control border border-ink-100 p-3 sm:grid-cols-2 lg:grid-cols-5">
                      <Select
                        value={metodo.metodo_termino_pago_id}
                        onChange={(evento) => {
                          const id = Number(evento.target.value);
                          const termino = catalogos.data.terminos_pago.find((t) => t.id === id);
                          actualizarMetodo(indice, { metodo_termino_pago_id: id, metodo_nombre: termino?.descripcion ?? '' });
                        }}
                      >
                        {catalogos.data.terminos_pago.map((termino) => (
                          <option key={termino.id} value={termino.id}>
                            {termino.descripcion}
                          </option>
                        ))}
                      </Select>

                      <Select
                        value={metodo.destino_banco_id}
                        onChange={(evento) => {
                          const id = Number(evento.target.value);
                          const banco = catalogos.data.bancos.find((b) => b.id === id);
                          actualizarMetodo(indice, { destino_banco_id: id, banco_nombre: banco?.descripcion ?? '' });
                        }}
                      >
                        {catalogos.data.bancos.map((banco) => (
                          <option key={banco.id} value={banco.id}>
                            {banco.descripcion}
                          </option>
                        ))}
                      </Select>

                      <Input
                        label="Referencia"
                        value={metodo.referencia ?? ''}
                        onChange={(evento) => actualizarMetodo(indice, { referencia: evento.target.value })}
                      />

                      <Input
                        label="Monto"
                        type="number"
                        min="0"
                        step="0.01"
                        value={metodo.monto}
                        onChange={(evento) => actualizarMetodo(indice, { monto: numero(evento.target.value) })}
                      />

                      <div className="flex items-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Quitar método"
                          onClick={() =>
                            setMetodosPago((anteriores) => anteriores.filter((_, i) => i !== indice))
                          }
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 ml-auto w-full max-w-sm">
                <FilaTotal
                  etiqueta="Suma de pagos"
                  valor={`S/ ${monto(totalMetodos)}`}
                  advertencia={Math.abs(totalMetodos - totales.total) > 0.009}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => navegar('/gestion/cotizaciones')}
            >
              Cancelar
            </Button>
            <Button loading={crear.isPending} onClick={registrar}>
              Registrar cotización
            </Button>
          </div>
        </>
      ) : null}

      <Dialog
        open={modalAbierto}
        onClose={() => setModalAbierto(false)}
        title="Agregar producto"
        description="Selecciona el combustible, la cantidad y el precio."
        size="sm"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setModalAbierto(false)}>
              Cerrar
            </Button>
            <Button size="sm" onClick={agregarProducto} disabled={!productoElegido}>
              Agregar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Campo etiqueta="Producto">
            <Select value={productoId} onChange={(evento) => elegirProducto(evento.target.value)}>
              <option value="">Seleccionar</option>
              {catalogos.data?.productos.map((producto) => (
                <option key={producto.id} value={producto.id}>
                  {producto.nombre} ({producto.unidad_medida})
                </option>
              ))}
            </Select>
          </Campo>

          <Campo etiqueta="Precio unitario (inc. IGV)">
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={precioUnitario}
              onChange={(evento) => setPrecioUnitario(evento.target.value)}
            />
          </Campo>

          <Campo etiqueta="Cantidad (GLN/LTR)">
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={cantidad}
              onChange={(evento) => setCantidad(evento.target.value)}
            />
          </Campo>

          <Campo etiqueta="Total (S/.)">
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Se calcula solo"
              value={totalManual}
              onChange={(evento) => setTotalManual(evento.target.value)}
            />
          </Campo>

          {productoElegido && cantidad ? (
            <p className="text-xs text-ink-500">
              Cálculo: {monto(numero(precioUnitario))} × {monto(numero(cantidad))} ={' '}
              <strong>S/ {monto(numero(totalManual) || numero(precioUnitario) * numero(cantidad))}</strong>
            </p>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}

interface CampoProps {
  etiqueta: string;
  children: ReactNode;
}

function Campo({ etiqueta, children }: CampoProps) {
  return (
    <label className="block text-xs font-medium text-ink-500">
      <span className="mb-1 block">{etiqueta}</span>
      {children}
    </label>
  );
}

function FilaTotal({
  etiqueta,
  valor,
  destacado = false,
  advertencia = false,
}: {
  etiqueta: string;
  valor: string;
  destacado?: boolean;
  advertencia?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between border-b border-ink-50 py-1 ${
        destacado ? 'font-bold text-ink-900' : 'text-ink-600'
      } ${advertencia ? 'text-rose-600' : ''}`}
    >
      <span>{etiqueta}</span>
      <span className="tabular-nums">{valor}</span>
    </div>
  );
}
